import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { t, getLanguage } from './i18n.js';
import { getOperatingStatus, isBookmarked } from './storage.js';
import { getTranslatedCenterName, getTranslatedAddress } from './translator.js';

let mapInstance = null;
let currentTileLayer = null;
let markersLayer = null;
let regionLabelsLayer = null;
let userMarker = null;
let activeMarkerId = null;

const statusColors = {
  open: '#10B981',     // Emerald green
  closed: '#EF4444',   // Rose red
  holiday: '#F59E0B'   // Amber yellow
};

// Major Korean hubs & tourist cities for multilingual map overlay
const MAJOR_REGIONS = [
  { name: { ko: "서울", en: "Seoul", ja: "ソウル", zh: "首尔" }, lat: 37.5665, lng: 126.9780, type: "capital" },
  { name: { ko: "부산", en: "Busan", ja: "釜山", zh: "釜山" }, lat: 35.1796, lng: 129.0756, type: "major" },
  { name: { ko: "제주", en: "Jeju", ja: "済州", zh: "济州" }, lat: 33.4996, lng: 126.5312, type: "major" },
  { name: { ko: "인천", en: "Incheon", ja: "仁川", zh: "仁川" }, lat: 37.4563, lng: 126.7052, type: "major" },
  { name: { ko: "대구", en: "Daegu", ja: "大邱", zh: "大邱" }, lat: 35.8714, lng: 128.6014, type: "city" },
  { name: { ko: "대전", en: "Daejeon", ja: "大田", zh: "大田" }, lat: 36.3504, lng: 127.3845, type: "city" },
  { name: { ko: "광주", en: "Gwangju", ja: "光州", zh: "光州" }, lat: 35.1595, lng: 126.8526, type: "city" },
  { name: { ko: "울산", en: "Ulsan", ja: "蔚山", zh: "蔚山" }, lat: 35.5384, lng: 129.3114, type: "city" },
  { name: { ko: "경주", en: "Gyeongju", ja: "慶州", zh: "庆州" }, lat: 35.8562, lng: 129.2247, type: "tour" },
  { name: { ko: "전주", en: "Jeonju", ja: "全州", zh: "全州" }, lat: 35.8242, lng: 127.1480, type: "tour" },
  { name: { ko: "강릉", en: "Gangneung", ja: "江陵", zh: "江陵" }, lat: 37.7519, lng: 128.8761, type: "tour" },
  { name: { ko: "속초", en: "Sokcho", ja: "束草", zh: "束草" }, lat: 38.2070, lng: 128.5918, type: "tour" },
  { name: { ko: "여수", en: "Yeosu", ja: "麗水", zh: "丽水" }, lat: 34.7604, lng: 127.6622, type: "tour" },
  { name: { ko: "춘천", en: "Chuncheon", ja: "春川", zh: "春川" }, lat: 37.8813, lng: 127.7298, type: "tour" },
  { name: { ko: "수원", en: "Suwon", ja: "水原", zh: "水原" }, lat: 37.2636, lng: 127.0286, type: "city" },
  { name: { ko: "안동", en: "Andong", ja: "安東", zh: "安东" }, lat: 36.5684, lng: 128.7294, type: "tour" },
  { name: { ko: "포항", en: "Pohang", ja: "浦項", zh: "浦项" }, lat: 36.0190, lng: 129.3435, type: "city" }
];

function getTileConfig(lang) {
  if (lang === 'en') {
    return {
      url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
      attribution: '&copy; <a href="https://carto.com/">CARTO</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19
    };
  }
  return {
    url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    maxZoom: 19
  };
}

export function initMap(containerId = 'map', initialLat = 36.3, initialLng = 127.8, initialZoom = 7) {
  if (mapInstance) {
    mapInstance.remove();
  }

  const lang = getLanguage();
  const tileConfig = getTileConfig(lang);

  mapInstance = L.map(containerId, {
    center: [initialLat, initialLng],
    zoom: initialZoom,
    zoomControl: false
  });

  L.control.zoom({ position: 'bottomright' }).addTo(mapInstance);

  currentTileLayer = L.tileLayer(tileConfig.url, {
    attribution: tileConfig.attribution,
    maxZoom: tileConfig.maxZoom
  }).addTo(mapInstance);

  regionLabelsLayer = L.layerGroup().addTo(mapInstance);
  markersLayer = L.layerGroup().addTo(mapInstance);

  renderRegionLabels(lang);

  return mapInstance;
}

export function renderRegionLabels(lang = 'ko') {
  if (!mapInstance || !regionLabelsLayer) return;
  regionLabelsLayer.clearLayers();

  MAJOR_REGIONS.forEach(region => {
    const labelText = region.name[lang] || region.name.en || region.name.ko;
    const subText = lang !== 'ko' ? region.name.ko : '';

    const labelHtml = `
      <div class="map-region-label ${region.type}">
        <span class="main-label">${labelText}</span>
        ${subText ? `<span class="sub-label">${subText}</span>` : ''}
      </div>
    `;

    const icon = L.divIcon({
      className: 'custom-region-icon',
      html: labelHtml,
      iconSize: [80, 30],
      iconAnchor: [40, 15]
    });

    const marker = L.marker([region.lat, region.lng], {
      icon: icon,
      interactive: false,
      zIndexOffset: -500 // Keep behind tourist center pins
    });

    regionLabelsLayer.addLayer(marker);
  });
}

export function updateMapTileLayer(lang) {
  if (!mapInstance) return;
  const config = getTileConfig(lang);
  if (currentTileLayer) {
    mapInstance.removeLayer(currentTileLayer);
  }
  currentTileLayer = L.tileLayer(config.url, {
    attribution: config.attribution,
    maxZoom: config.maxZoom
  }).addTo(mapInstance);

  // Update multilingual region overlays
  renderRegionLabels(lang);

  if (markersLayer) {
    markersLayer.bringToFront();
  }
}

export function updateMapMarkers(centers, onSelectCenter) {
  if (!mapInstance || !markersLayer) return;

  markersLayer.clearLayers();

  centers.forEach(center => {
    if (!center.lat || !center.lng) return;

    const op = getOperatingStatus(center);
    const color = statusColors[op.status] || '#3B82F6';

    const iconHtml = `
      <div class="custom-marker-pin ${op.status}" style="--pin-color: ${color}">
        <div class="marker-dot"></div>
        <div class="marker-pulse"></div>
      </div>
    `;

    const customIcon = L.divIcon({
      className: 'custom-leaflet-marker',
      html: iconHtml,
      iconSize: [28, 28],
      iconAnchor: [14, 14],
      popupAnchor: [0, -14]
    });

    const marker = L.marker([center.lat, center.lng], { icon: customIcon });

    marker.on('click', () => {
      activeMarkerId = center.id;
      if (onSelectCenter) onSelectCenter(center);
    });

    marker.bindPopup(() => createPopupContent(center), {
      className: 'custom-glass-popup',
      maxWidth: 320
    });

    markersLayer.addLayer(marker);
  });
}

function createPopupContent(center) {
  const currentLang = getLanguage();
  const op = getOperatingStatus(center);
  const statusLabel = t(op.textKey);
  const bookmarked = isBookmarked(center.id);

  const displayName = getTranslatedCenterName(center, currentLang);
  const displayAddress = getTranslatedAddress(center, currentLang);

  const langBadges = [];
  if (center.languages.en) langBadges.push('<span class="badge badge-lang">EN</span>');
  if (center.languages.ja) langBadges.push('<span class="badge badge-lang">JA</span>');
  if (center.languages.zh) langBadges.push('<span class="badge badge-lang">ZH</span>');

  const facilityBadges = [];
  if (center.facilities.luggage) facilityBadges.push(`<span class="badge badge-facility">🧳 ${t('facLuggage')}</span>`);
  if (center.facilities.wheelchair) facilityBadges.push(`<span class="badge badge-facility">♿ ${t('facWheelchair')}</span>`);
  if (center.facilities.stamp) facilityBadges.push(`<span class="badge badge-facility">🏅 ${t('facStamp')}</span>`);

  return `
    <div class="map-popup-inner">
      <div class="popup-header">
        <span class="status-chip chip-${op.status}">${statusLabel} (${op.hours})</span>
        <button class="btn-popup-fav ${bookmarked ? 'active' : ''}" data-id="${center.id}" title="${t('bookmark')}">
          ${bookmarked ? '★' : '☆'}
        </button>
      </div>
      <h4 class="popup-title">${displayName}</h4>
      <p class="popup-address">📍 ${displayAddress}</p>
      
      <div class="popup-badges">
        ${langBadges.join('')}
        ${facilityBadges.join('')}
      </div>

      <div class="popup-actions">
        ${center.phone ? `
          <a href="tel:${center.phone}" class="btn-popup-action btn-call">
            📞 ${center.phone}
          </a>
        ` : ''}
        <div class="popup-nav-links">
          <button class="btn-nav-mini btn-popup-taxi" data-id="${center.id}" style="background: #F59E0B; color: #FFF; border: none; cursor: pointer; font-weight: 700;">🚖 Taxi</button>
          <a href="https://map.kakao.com/link/to/${encodeURIComponent(center.name)},${center.lat},${center.lng}" target="_blank" rel="noopener" class="btn-nav-mini kakao" title="카카오맵">Kakao</a>
          <a href="https://map.naver.com/v5/search/${encodeURIComponent(center.name)}" target="_blank" rel="noopener" class="btn-nav-mini naver" title="네이버지도">Naver</a>
          <a href="https://www.google.com/maps/dir/?api=1&destination=${center.lat},${center.lng}" target="_blank" rel="noopener" class="btn-nav-mini google" title="구글맵">Google</a>
        </div>
      </div>
    </div>
  `;
}

export function flyToCenter(latOrCenter, lngOrZoom = 15, zoomOpt = 15) {
  if (!mapInstance) return;
  let lat, lng, zoom = 15;
  if (typeof latOrCenter === 'object' && latOrCenter !== null) {
    lat = latOrCenter.lat;
    lng = latOrCenter.lng;
    zoom = typeof lngOrZoom === 'number' ? lngOrZoom : 15;
  } else {
    lat = latOrCenter;
    lng = lngOrZoom;
    zoom = zoomOpt;
  }
  if (!lat || !lng) return;
  mapInstance.flyTo([lat, lng], zoom, {
    animate: true,
    duration: 1.2
  });
}

export function setUserLocation(lat, lng) {
  if (!mapInstance) return;

  if (userMarker) {
    userMarker.setLatLng([lat, lng]);
  } else {
    const userIcon = L.divIcon({
      className: 'user-location-marker',
      html: `
        <div class="user-pulse-wrap">
          <div class="user-dot"></div>
          <div class="user-ring"></div>
        </div>
      `,
      iconSize: [24, 24],
      iconAnchor: [12, 12]
    });

    userMarker = L.marker([lat, lng], { icon: userIcon }).addTo(mapInstance);
  }

  mapInstance.flyTo([lat, lng], 13, { duration: 1.5 });
}
